import type { MetricasDoPeriodo } from "@base/contracts";

export type IdDoIndicador =
  | "faturamento-consulta"
  | "faturamento-tratamento"
  | "investimento"
  | "lucro"
  | "ticket-medio"
  | "roas"
  | "valor-em-aberto";

export type Indicador = {
  readonly id: IdDoIndicador;
  readonly rotulo: string;
  readonly unidade: "moeda" | "multiplicador";
  /** `null` quando a conta nao existe (ex.: ticket medio sem venda). */
  readonly valor: number | null;
  /** Variacao contra o periodo anterior, em fracao (0.12 = +12%). */
  readonly variacao: number | null;
  /** Subir e bom? Investimento subir nao e bom nem ruim por si so. */
  readonly sentido: "maior-melhor" | "neutro";
};

type Calculo = {
  readonly id: IdDoIndicador;
  readonly rotulo: string;
  readonly unidade: Indicador["unidade"];
  readonly sentido: Indicador["sentido"];
  readonly calcular: (m: MetricasDoPeriodo) => number | null;
};

const dividir = (a: number, b: number | null): number | null => (b !== null && b > 0 ? a / b : null);
const emReais = (centavos: number | null): number | null => (centavos === null ? null : centavos / 100);

/**
 * Os seis indicadores do topo do Dashboard, calculados dos numeros base.
 * Valores em reais (nao centavos) e ROAS como multiplicador.
 */
const CALCULOS: readonly Calculo[] = [
  {
    id: "faturamento-consulta",
    rotulo: "Faturamento de consulta",
    unidade: "moeda",
    sentido: "maior-melhor",
    calcular: (m) => m.faturamentoConsultaCentavos / 100,
  },
  {
    id: "faturamento-tratamento",
    rotulo: "Faturamento de tratamento",
    unidade: "moeda",
    sentido: "maior-melhor",
    calcular: (m) => m.faturamentoTratamentoCentavos / 100,
  },
  { id: "investimento", rotulo: "Investimento", unidade: "moeda", sentido: "neutro", calcular: (m) => emReais(m.investimentoCentavos) },
  {
    id: "lucro",
    rotulo: "Lucro",
    unidade: "moeda",
    sentido: "maior-melhor",
    // Sem fonte de midia, nao ha investimento conhecido: lucro fica vazio, nao
    // vira o faturamento inteiro (seria um lucro inventado).
    calcular: (m) =>
      m.investimentoCentavos === null ? null : (m.faturamentoCentavos - m.investimentoCentavos) / 100,
  },
  {
    id: "ticket-medio",
    rotulo: "Ticket médio",
    unidade: "moeda",
    sentido: "maior-melhor",
    calcular: (m) => {
      const ticket = dividir(m.faturamentoCentavos, m.vendas);
      return ticket === null ? null : ticket / 100;
    },
  },
  {
    id: "roas",
    rotulo: "ROAS",
    unidade: "multiplicador",
    sentido: "maior-melhor",
    calcular: (m) => dividir(m.faturamentoCentavos, m.investimentoCentavos),
  },
  {
    id: "valor-em-aberto",
    rotulo: "Valor em aberto",
    unidade: "moeda",
    sentido: "maior-melhor",
    calcular: (m) => emReais(m.valorEmAbertoCentavos),
  },
];

/** As duas plataformas conectadas, em reais. `null` quando falta uma delas. */
export function divisaoDoInvestimento(
  metricas: Pick<MetricasDoPeriodo, "metaCentavos" | "googleCentavos">,
): { readonly meta: number; readonly google: number } | null {
  if (metricas.metaCentavos == null || metricas.googleCentavos == null) return null;
  return { meta: metricas.metaCentavos / 100, google: metricas.googleCentavos / 100 };
}

export function calcularIndicadores(
  atual: MetricasDoPeriodo,
  anterior: MetricasDoPeriodo,
): readonly Indicador[] {
  return CALCULOS.map((calculo) => {
    const valor = calculo.calcular(atual);
    const base = calculo.calcular(anterior);
    return {
      id: calculo.id,
      rotulo: calculo.rotulo,
      unidade: calculo.unidade,
      sentido: calculo.sentido,
      valor,
      // Variacao sobre base negativa (lucro de prejuizo) inverte o sinal e
      // engana; nesse caso nao mostramos variacao.
      variacao: valor === null || base === null || base <= 0 ? null : (valor - base) / base,
    };
  });
}

/**
 * Metas do periodo vindas do Funil Projetado, em reais. A forma e definida
 * aqui (quem consome define a porta); o modulo de metas so precisa encaixar.
 */
export type MetasDoPeriodo = {
  readonly faturamentoReais: number;
  readonly investimentoReais: number;
  readonly lucroReais: number;
  readonly ticketMedioReais: number | null;
  readonly roas: number | null;
  /** Meta de cada etapa do funil no periodo. */
  readonly funil?: {
    readonly gerenciador: number;
    readonly crm: number;
    readonly mql: number;
    readonly agendado: number;
    readonly consulta: number;
    readonly venda: number;
  };
  /** Orcamento do periodo, base dos custos por etapa da meta. */
  readonly investimentoCentavos?: number;
};

/** O que o Dashboard recebe do Funil Projetado para o periodo. */
export type MetasNoPainel = {
  /** `null` quando algum mes do periodo nao foi planejado. */
  readonly meta: MetasDoPeriodo | null;
  /** Meses do periodo sem Funil Projetado ("outubro/2026"), para avisar. */
  readonly mesesSemPlanejamento: readonly string[];
  readonly carregando: boolean;
};

/** Meta de cada cartao; Valor em aberto nao tem meta. */
export function metaDoIndicador(id: IdDoIndicador, metas: MetasDoPeriodo | null): number | null {
  if (!metas) return null;
  switch (id) {
    case "faturamento-consulta":
      return null;
    case "faturamento-tratamento":
      return metas.faturamentoReais;
    case "investimento":
      return metas.investimentoReais;
    case "lucro":
      return metas.lucroReais;
    case "ticket-medio":
      return metas.ticketMedioReais;
    case "roas":
      return metas.roas;
    case "valor-em-aberto":
      return null;
  }
}
